// components/ImportNotesZipModal.jsx
// Dialog impor: pilih berkas .zip berisi berkas .md -> pratinjau calon catatan
// (checkbox per catatan + pilihan scope) -> buat catatan lewat API halaman
// tujuan. Dipakai bersama oleh halaman Notes (RAG) dan Notes Plain.
//
// Pemetaan zip mengikuti format hasil "Download ZIP":
//   - folder            = 1 catatan bertab (tiap .md di dalamnya jadi 1 tab)
//   - .md di root zip   = 1 catatan tanpa tab
// Front-matter YAML (note / tab / scope) dipakai kalau ada. Catatan lama TIDAK
// pernah ditimpa: setiap baris di pratinjau selalu jadi catatan BARU.
import { useState, useEffect } from 'react';
import { zipToNotes } from '../utils/notesExport';
import { humanBytes } from '../utils/imageAttach';
import { X, RefreshCw, FileUp, AlertTriangle, CheckCircle2, Layers } from 'lucide-react';

export default function ImportNotesZipModal({ file, endpoint, api, id, targetLabel, onClose, onDone }) {
  const [parsing, setParsing]     = useState(true);
  const [err, setErr]             = useState('');
  const [items, setItems]         = useState([]);
  const [sel, setSel]             = useState(() => new Set());
  const [scopeMode, setScopeMode] = useState('file');   // file | private | common
  const [busy, setBusy]           = useState(false);
  const [done, setDone]           = useState(0);
  const [fails, setFails]         = useState([]);

  useEffect(() => {
    let alive = true;
    setParsing(true); setErr(''); setItems([]); setFails([]); setDone(0);
    zipToNotes(file)
      .then((list) => {
        if (!alive) return;
        setItems(list);
        const s = new Set();
        list.forEach((n, i) => { if (n.title || n.content || n.tabs.length) s.add(i); });
        setSel(s);
        setParsing(false);
      })
      .catch((e) => { if (!alive) return; setErr((e && e.message) || String(e)); setParsing(false); });
    return () => { alive = false; };
  }, [file]);

  const usable = (n) => Boolean(n.title || n.content || n.tabs.length);
  const usableIdx = items.map((n, i) => (usable(n) ? i : -1)).filter(i => i >= 0);
  const allSelected = usableIdx.length > 0 && usableIdx.every(i => sel.has(i));

  const toggle = (i) => setSel((prev) => {
    const s = new Set(prev);
    if (s.has(i)) s.delete(i); else s.add(i);
    return s;
  });
  const toggleAll = () => setSel((prev) => {
    const s = new Set(prev);
    if (allSelected) usableIdx.forEach(i => s.delete(i));
    else usableIdx.forEach(i => s.add(i));
    return s;
  });

  const picked = items.filter((_, i) => sel.has(i) && usable(items[i]));

  const run = async () => {
    if (!picked.length || busy) return;
    setBusy(true); setDone(0); setFails([]);
    const failed = [];
    let ok = 0;
    for (let i = 0; i < picked.length; i++) {
      const n = picked[i];
      const scope = scopeMode === 'file' ? n.scope : scopeMode;
      try {
        await api(endpoint, {
          method: 'POST',
          body: JSON.stringify({
            title: n.title,
            content: n.content,
            scope,
            tabs: (n.tabs || []).map(t => ({ title: t.title, content: t.content })),
          }),
        });
        ok++;
      } catch (e) {
        failed.push({ title: n.title || n.source, msg: (e && e.message) || String(e) });
      }
      setDone(i + 1);
    }
    setBusy(false);
    setFails(failed);
    onDone({ ok, failed: failed.length, keepOpen: failed.length > 0 });
  };

  const scopeOpts = [
    ['file',    id ? 'Ikuti berkas'  : 'From file'],
    ['private', id ? 'Semua Private' : 'All Private'],
    ['common',  id ? 'Semua Shared'  : 'All Shared'],
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">

        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <FileUp style={{ width: 16, height: 16 }} className="text-lime-600" />
          <div className="min-w-0">
            <div className="font-semibold text-slate-700 text-sm">
              {(id ? 'Impor ZIP ke ' : 'Import ZIP into ') + targetLabel}
            </div>
            <div className="text-[11px] text-slate-400 truncate">{file && file.name}</div>
          </div>
          <button onClick={() => !busy && onClose()} disabled={busy}
            className="ml-auto w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-40">
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>

        {parsing ? (
          <div className="px-4 py-12 text-center text-slate-400 text-sm">
            <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin mx-auto mb-2" />
            {id ? 'Membaca isi ZIP...' : 'Reading ZIP...'}
          </div>
        ) : err ? (
          <div className="px-4 py-8 text-center">
            <AlertTriangle style={{ width: 20, height: 20 }} className="text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600">{err}</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/70 flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={busy}
                  className="w-4 h-4 accent-lime-600" />
                {(id ? 'Pilih semua' : 'Select all') + ' (' + usableIdx.length + ')'}
              </label>
              <span className="text-xs text-slate-500">
                {picked.length + (id ? ' akan diimpor' : ' to import')}
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <span className="text-[11px] text-slate-400">Scope</span>
                {scopeOpts.map(([k, lbl]) => (
                  <button key={k} onClick={() => setScopeMode(k)} disabled={busy}
                    className={'px-2 py-1 text-[11px] font-semibold rounded-lg border transition-colors '
                      + (scopeMode === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50')}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-y-auto divide-y divide-slate-50">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-400 text-sm">
                  {id ? 'Tidak ada catatan yang bisa diimpor.' : 'Nothing to import.'}
                </div>
              ) : items.map((n, i) => {
                const ok = usable(n);
                return (
                  <div key={n.source + '#' + i} className={'flex items-start gap-3 px-4 py-2.5 ' + (ok ? '' : 'opacity-45')}>
                    <input type="checkbox" checked={sel.has(i)} disabled={!ok || busy}
                      onChange={() => toggle(i)} className="mt-0.5 w-4 h-4 accent-lime-600" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-700 truncate">
                        {n.title || (id ? '(tanpa judul)' : '(untitled)')}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate">{n.source}</div>
                    </div>
                    <div className="shrink-0 text-[11px] text-slate-500 text-right leading-relaxed">
                      {n.tabs.length > 0 && (
                        <span className="inline-flex items-center gap-1 mr-2 text-violet-600">
                          <Layers style={{ width: 11, height: 11 }} />{n.tabs.length + ' tab'}
                        </span>
                      )}
                      {humanBytes(n.bytes)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {fails.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-100 bg-rose-50/70 text-[11px] text-rose-700 max-h-28 overflow-y-auto">
            <div className="font-semibold mb-1 flex items-center gap-1">
              <AlertTriangle style={{ width: 12, height: 12 }} />
              {(id ? 'Gagal: ' : 'Failed: ') + fails.length}
            </div>
            {fails.map((f, i) => (<div key={i} className="truncate">{(f.title || '-') + ' - ' + f.msg}</div>))}
          </div>
        )}

        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2">
          {busy && (
            <span className="text-xs text-slate-500 flex items-center gap-1.5">
              <RefreshCw style={{ width: 12, height: 12 }} className="animate-spin" />
              {done + ' / ' + picked.length}
            </span>
          )}
          {!busy && done > 0 && fails.length === 0 && (
            <span className="text-xs text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 style={{ width: 13, height: 13 }} />
              {done + (id ? ' catatan dibuat' : ' notes created')}
            </span>
          )}
          <button onClick={() => !busy && onClose()} disabled={busy}
            className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40">
            {id ? 'Tutup' : 'Close'}
          </button>
          <button onClick={run} disabled={busy || parsing || Boolean(err) || picked.length === 0}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-lime-600 text-white hover:bg-lime-700 disabled:opacity-40">
            {(id ? 'Impor' : 'Import') + (picked.length ? ' (' + picked.length + ')' : '')}
          </button>
        </div>
      </div>
    </div>
  );
}
